import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Clock } from 'lucide-react';

const FALLBACK_IMG = 'https://images.unsplash.com/photo-1600100397608-f010f423b971?q=80&w=800';
const handleImgError = (e) => { if (e.target.src !== FALLBACK_IMG) e.target.src = FALLBACK_IMG; };

export function NewsCard({ article, variant = 'default' }) {
  const { t, isKannada } = useLanguage();
  const title = isKannada ? (article.title_kn || article.title) : article.title;
  const summary = isKannada ? (article.summary_kn || article.summary) : article.summary;
  const catName = article.category ? (isKannada ? article.category.name_kn : article.category.name) : '';
  const authorLabel = article.author_label || article.author?.name || 'ತಾಯ್ನಾಡು ಸುದ್ದಿ ಮನೆ';

  const formatDate = (d) => d ? new Date(d).toLocaleDateString(isKannada ? 'kn-IN' : 'en-IN', { day: '2-digit', month: 'short' }) : '';

  if (variant === 'compact') {
    return (
      <Link to={`/article/${article.slug || article.article_id}`}
        className="group flex gap-3 py-3 border-b border-border/50 last:border-0"
        data-testid={`news-card-compact-${article.article_id}`}>
        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 img-zoom">
          <img src={article.featured_image || FALLBACK_IMG} alt="" className="w-full h-full object-cover" loading="lazy" onError={handleImgError} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`text-sm font-bold text-foreground group-hover:text-palace-500 transition-colors line-clamp-2 ${isKannada ? 'font-kannada' : ''}`}>{title}</h4>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>{formatDate(article.published_at)}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link to={`/article/${article.slug || article.article_id}`}
        className="group flex gap-4 p-4 rounded-xl neu-card ripple-effect"
        data-testid={`news-card-horizontal-${article.article_id}`}>
        <div className="w-32 h-24 rounded-lg overflow-hidden flex-shrink-0 img-zoom">
          <img src={article.featured_image || FALLBACK_IMG} alt="" className="w-full h-full object-cover" loading="lazy" onError={handleImgError} />
        </div>
        <div className="flex-1 min-w-0">
          {catName && <span className="text-[10px] font-accent font-bold uppercase tracking-[0.15em] text-palace-500">{catName}</span>}
          <h3 className={`text-base font-bold text-foreground group-hover:text-palace-500 transition-colors line-clamp-2 mt-0.5 ${isKannada ? 'font-kannada' : ''}`}>{title}</h3>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(article.published_at)}</span>
            <span>{authorLabel}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/article/${article.slug || article.article_id}`}
      className="group block rounded-xl overflow-hidden holo-card glow-border ripple-effect"
      data-testid={`news-card-${article.article_id}`}>
      <div className="relative overflow-hidden aspect-[16/10] img-zoom">
        <img src={article.featured_image || FALLBACK_IMG} alt="" className="w-full h-full object-cover" loading="lazy" onError={handleImgError} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        {article.featured && (
          <span className="absolute top-3 left-3 px-2.5 py-0.5 metallic-gold text-palace-900 text-[10px] font-bold font-accent uppercase tracking-[0.15em] rounded-full neon-gold">{t('featured')}</span>
        )}
        {catName && (
          <span className="absolute bottom-3 left-3 px-2.5 py-0.5 glass text-white text-[10px] font-accent font-bold uppercase tracking-[0.15em] rounded-full">{catName}</span>
        )}
        <div className="absolute inset-0 flex items-end p-4 opacity-0 group-hover:opacity-100 transition-all duration-400">
          <div className="glass-card rounded-lg p-3 w-full">
            <p className={`text-xs text-white/90 line-clamp-3 ${isKannada ? 'font-kannada' : ''}`}>{summary}</p>
          </div>
        </div>
      </div>
      <div className="p-4 relative z-10">
        <h3 className={`text-base font-bold text-foreground group-hover:text-palace-500 transition-colors line-clamp-2 leading-snug ${isKannada ? 'font-kannada' : 'font-heading'}`}>{title}</h3>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
          <span className="text-xs text-muted-foreground">{authorLabel}</span>
          <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(article.published_at)}</span>
        </div>
      </div>
    </Link>
  );
}
